import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash-es";
import SearchIcon from "@mui/icons-material/Search";
import ToolbarSelect from "components/Common/ToolbarSelect";
import { MenuItem } from "@mui/material";
import type { ZnsTemplateStatus, ZnsTemplateTag } from "../types/znsTemplate";
import DateRangePicker from "components/Common/DateRangePicker";

const SEARCH_DEBOUNCE_MS = 400;

export interface TemplateFilters {
  tag: "" | ZnsTemplateTag;
  status: "" | ZnsTemplateStatus;
  createdDateFrom: string;
  createdDateTo: string;
  pulledDateFrom: string;
  pulledDateTo: string;
}

interface TemplateToolbarProps {
  title?: string;
  /** Current purpose/tag filter: empty = All, or TRANSACTION | CUSTOMER_CARE | PROMOTION */
  tag?: "" | ZnsTemplateTag;
  onTagChange?: (tag: "" | ZnsTemplateTag) => void;
  /** Current status filter: empty = All, or DISABLE | REJECT | DELETE | PENDING_REVIEW | ENABLE */
  status?: "" | ZnsTemplateStatus;
  onStatusChange?: (status: "" | ZnsTemplateStatus) => void;
  /** Date range for Creation Date filter (ISO strings for API) */
  dateRange?: { from: string; to: string };
  onDateRangeChange?: (from: string, to: string) => void;
  /** Date range for Modified Date filter (ISO strings for API) */
  pulledDateRange?: { from: string; to: string };
  onPulledDateRangeChange?: (from: string, to: string) => void;
  /** Search term for Name filter */
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Called when reset filters is clicked */
  onReset?: () => void;
}

const PURPOSE_OPTIONS: { value: "all" | ZnsTemplateTag; label: string }[] = [
  { value: "all", label: "All" },
  { value: "TRANSACTION", label: "TRANSACTION" },
  { value: "CUSTOMER_CARE", label: "CUSTOMER_CARE" },
  { value: "PROMOTION", label: "PROMOTION" },
];

const STATUS_OPTIONS: { value: "all" | ZnsTemplateStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "ENABLE", label: "ENABLE" },
  { value: "DISABLE", label: "DISABLE" },
  { value: "PENDING_REVIEW", label: "PENDING_REVIEW" },
  { value: "REJECT", label: "REJECT" },
  { value: "DELETE", label: "DELETE" },
];

/**
 * Toolbar for ZNS template list: search, purpose (tag), status, and date range filters.
 */
const TemplateToolbar: React.FC<TemplateToolbarProps> = ({
  title,
  tag = "",
  onTagChange,
  status = "",
  onStatusChange,
  dateRange = { from: "", to: "" },
  onDateRangeChange,
  pulledDateRange = { from: "", to: "" },
  onPulledDateRangeChange,
  search = "",
  onSearchChange,
  onReset,
}) => {
  const [searchInput, setSearchInput] = useState(search);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearchChangeRef.current?.(value);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  return (
    <Box sx={{ p: 2 }}>
      {title && <Typography variant="h5">{title}</Typography>}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Grid
          sx={{
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            id="template-search"
            sx={{ m: 1, minWidth: 400 }}
            size="small"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              slotProps: { input: { style: { padding: "8px" } } },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            placeholder="Search any template"
          />
          <ToolbarSelect
            label="ZNS Purpose"
            value={tag || "all"}
            onChange={(v) =>
              onTagChange?.(v === "all" ? "" : (v as ZnsTemplateTag))
            }
          >
            {PURPOSE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </ToolbarSelect>
          <ToolbarSelect
            label="Status"
            value={status || "all"}
            onChange={(v) =>
              onStatusChange?.(v === "all" ? "" : (v as ZnsTemplateStatus))
            }
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </ToolbarSelect>
          <Box sx={{ m: 1, display: "flex", alignItems: "center" }}>
            <DateRangePicker
              label="Creation Date"
              value={dateRange}
              onChange={(from, to) => onDateRangeChange?.(from, to)}
              format="DD/MM/YYYY"
              size="small"
              minWidth={220}
            />
          </Box>
          <Box sx={{ m: 1, display: "flex", alignItems: "center" }}>
            <DateRangePicker
              label="Pulled Date"
              value={pulledDateRange}
              onChange={(from, to) => onPulledDateRangeChange?.(from, to)}
              format="DD/MM/YYYY"
              size="small"
              minWidth={220}
            />
          </Box>
          {onReset && (
            <Button
              variant="outlined"
              onClick={onReset}
              sx={{
                height: 40,
                borderColor: "grey.400",
                color: "text.primary",
                fontWeight: "bold",
                ml: 1,
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default React.memo(TemplateToolbar);
